import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
} from 'firebase/firestore';
import { db } from './config';
import { getErrorMessage } from '../utils/errorUtils';

function normalizeString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function normalizeStatus(value) {
    return value === 'inactive' ? 'inactive' : 'active';
}

function normalizeValues(values) {
    if (!Array.isArray(values)) return [];
    const seen = new Set();
    return values.reduce((items, item) => {
        const draft = typeof item === 'string' ? { name: item } : item || {};
        const name = normalizeString(draft.name);
        if (!name || seen.has(name.toLowerCase())) return items;
        seen.add(name.toLowerCase());
        // NOTE: attribute-level price modifiers have been removed.
        // Pricing is managed at the product level (product.price, product.sizePrices).
        const value = {
            name,
            status: normalizeStatus(draft.status),
        };
        items.push(value);
        return items;
    }, []);
}

export async function getAllAttributes() {
    try {
        const q = query(collection(db, 'attributes'), orderBy('name'));
        const snapshot = await getDocs(q);
        return {
            attributes: snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
            error: null,
        };
    } catch (err) {
        console.error('Error fetching attributes:', err);
        return { attributes: [], error: getErrorMessage(err, 'Failed to fetch attributes. Please try again.') };
    }
}

export async function createAttribute(data = {}) {
    const name = normalizeString(data.name);
    if (!name) return { attributeId: null, error: 'Attribute name is required.' };

    const validTypes = ['checkbox', 'radio', 'select', 'text', 'color'];
    const type = validTypes.includes(data.type) ? data.type : 'select';

    try {
        const ref = await addDoc(collection(db, 'attributes'), {
            name,
            type,
            status: normalizeStatus(data.status),
            values: normalizeValues(data.values),
            subcategoryIds: Array.isArray(data.subcategoryIds) ? data.subcategoryIds.filter(Boolean) : [],
            subcategoryNames: Array.isArray(data.subcategoryNames) ? data.subcategoryNames.filter(Boolean) : [],
            categoryIds: Array.isArray(data.categoryIds) ? data.categoryIds.filter(Boolean) : [],
            categoryNames: Array.isArray(data.categoryNames) ? data.categoryNames.filter(Boolean) : [],
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
        return { attributeId: ref.id, error: null };
    } catch (err) {
        console.error('Error creating attribute:', err);
        return { attributeId: null, error: getErrorMessage(err, 'Failed to create attribute. Please try again.') };
    }
}

export async function updateAttribute(attributeId, updates = {}) {
    const payload = {};
    if (updates.name !== undefined) payload.name = normalizeString(updates.name);
    if (updates.status !== undefined) payload.status = normalizeStatus(updates.status);
    if (updates.type !== undefined) {
        const validTypes = ['checkbox', 'radio', 'select', 'text', 'color'];
        payload.type = validTypes.includes(updates.type) ? updates.type : 'select';
    }
    if (updates.values !== undefined) payload.values = normalizeValues(updates.values);
    if (updates.subcategoryIds !== undefined) {
        payload.subcategoryIds = Array.isArray(updates.subcategoryIds) ? updates.subcategoryIds.filter(Boolean) : [];
    }
    if (updates.subcategoryNames !== undefined) {
        payload.subcategoryNames = Array.isArray(updates.subcategoryNames) ? updates.subcategoryNames.filter(Boolean) : [];
    }
    if (updates.categoryIds !== undefined) {
        payload.categoryIds = Array.isArray(updates.categoryIds) ? updates.categoryIds.filter(Boolean) : [];
    }
    if (updates.categoryNames !== undefined) {
        payload.categoryNames = Array.isArray(updates.categoryNames) ? updates.categoryNames.filter(Boolean) : [];
    }
    if (payload.name !== undefined && !payload.name) return { success: false, error: 'Attribute name is required.' };

    try {
        await updateDoc(doc(db, 'attributes', attributeId), {
            ...payload,
            updatedAt: serverTimestamp(),
        });
        return { success: true, error: null };
    } catch (err) {
        console.error('Error updating attribute:', err);
        return { success: false, error: getErrorMessage(err, 'Failed to update attribute. Please try again.') };
    }
}

export async function deleteAttribute(attributeId) {
    try {
        await deleteDoc(doc(db, 'attributes', attributeId));
        return { success: true, error: null };
    } catch (err) {
        console.error('Error deleting attribute:', err);
        return { success: false, error: getErrorMessage(err, 'Failed to delete attribute. Please try again.') };
    }
}

/**
 * Get attributes assigned to a specific subcategory (by ID or name).
 */
export function getAttributesForSubcategory(attributes = [], subcategory) {
    if (!subcategory) return [];
    return attributes.filter((attribute) => {
        if ((attribute.status || 'active') !== 'active') return false;
        const ids = Array.isArray(attribute.subcategoryIds) ? attribute.subcategoryIds : [];
        const names = Array.isArray(attribute.subcategoryNames) ? attribute.subcategoryNames : [];
        return ids.includes(subcategory.id) || names.includes(subcategory.name);
    });
}

/**
 * Get attributes assigned to a specific category (by ID or name).
 */
export function getAttributesForCategory(attributes = [], categoryName, categoryId) {
    if (!categoryName) return [];
    return attributes.filter((attribute) => {
        if ((attribute.status || 'active') !== 'active') return false;
        const ids = Array.isArray(attribute.categoryIds) ? attribute.categoryIds : [];
        const names = Array.isArray(attribute.categoryNames) ? attribute.categoryNames : [];
        return ids.includes(categoryId) || names.includes(categoryName);
    });
}

/**
 * Get all matching attributes for a product form context.
 * For non-Clothing categories: includes both category-level and subcategory-level attrs.
 * For Clothing: uses subcategory-level only (existing behavior).
 */
export function getAttributesForProduct(attributes = [], categoryName, categoryId, subcategory) {
    if (!categoryName) return [];
    
    // Get category-level attributes
    const catAttrs = getAttributesForCategory(attributes, categoryName, categoryId);
    
    // Get subcategory-level attributes
    const subAttrs = getAttributesForSubcategory(attributes, subcategory);
    
    // Merge: subcategory attrs take priority (deduplicate by id)
    const merged = [...catAttrs];
    subAttrs.forEach((attr) => {
        if (!merged.find((a) => a.id === attr.id)) {
            merged.push(attr);
        }
    });
    return merged;
}